import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { SIGNOUTS } from "./signoutData";
import { useNavigate } from "react-router-dom";

function formatTime(ts) {
  const d = new Date(ts);
  const h = d.getHours(), m = d.getMinutes();
  return `${h % 12 || 12}:${m.toString().padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

function getElapsed(ts, now) {
  const mins = Math.floor((now - new Date(ts)) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min";
  return `${mins} min`;
}

const SchoolView = () => {
  const { schoolId, role, loading } = useUser();
  const navigate = useNavigate();
  const [signOuts, setSignOuts]   = useState([]);
  const [now, setNow]             = useState(() => Date.now());
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterGrade, setFilterGrade] = useState("all");

  // Access guard
  useEffect(() => {
    if (!loading && role !== "admin" && role !== "security") {
      navigate("/dashboard");
    }
  }, [role, loading, navigate]);

  // Refresh elapsed time every 15s
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  // All active sign-outs across the school — one listener on the top-level collection
  useEffect(() => {
    if (!schoolId) return;
    return onSnapshot(
      query(collection(firestore, SIGNOUTS), where("schoolId", "==", schoolId)),
      snap => setSignOuts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [schoolId]);

  const locations = [...new Set(signOuts.map(s => s.location))];
  const grades    = [...new Set(signOuts.map(s => s.grade).filter(Boolean))].sort();

  const filtered = signOuts
    .filter(s => filterLocation === "all" || s.location   === filterLocation)
    .filter(s => filterGrade    === "all" || s.grade      === filterGrade)
    .sort((a, b) => new Date(a.time_out) - new Date(b.time_out));

  if (loading) return <div>Loading…</div>;

  return (
    <div className="add-student-form-container">
      <h1>
        School Sign-Out Board
        {filtered.length > 0 && (
          <span style={{ fontSize: 16, fontWeight: 400, color: "var(--color-secondary)", marginLeft: 12 }}>
            {filtered.length} student{filtered.length !== 1 ? "s" : ""} out
          </span>
        )}
      </h1>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <select value={filterLocation} onChange={e => setFilterLocation(e.target.value)}>
          <option value="all">All Locations</option>
          {locations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
          <option value="all">All Grades</option>
          {grades.map(g => <option key={g} value={g}>Grade {g}</option>)}
        </select>
      </div>

      <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Student</th>
            <th>Grade</th>
            <th>Location</th>
            <th>Room</th>
            <th>Class</th>
            <th>Teacher</th>
            <th>Left At</th>
            <th>Time Out</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan="8" style={{ textAlign: "center", padding: 24, color: "var(--color-secondary)" }}>
                No students currently signed out.
              </td>
            </tr>
          ) : (
            filtered.map(s => {
              const mins   = Math.floor((now - new Date(s.time_out)) / 60000);
              const isLong = mins >= 10;
              return (
                <tr key={`${s.classId}-${s.id}`} style={{ background: isLong ? "#fff8e1" : undefined }}>
                  <td style={{ fontWeight: 600 }}>{s.studentName}</td>
                  <td>{s.grade}</td>
                  <td>{s.location}</td>
                  <td>{s.room || "—"}</td>
                  <td>{s.homeroom || "—"}</td>
                  <td>{s.teacherName || "—"}</td>
                  <td>{formatTime(s.time_out)}</td>
                  <td style={{ color: isLong ? "#b45309" : "inherit", fontWeight: isLong ? 600 : 400 }}>
                    {getElapsed(s.time_out, now)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default SchoolView;
